import React, { useState } from "react";
import { toast } from "react-toastify";
import { highlightMatch } from "./highlightmatch";


interface TextCopyProps {
  text: string;
  searchQuery?: string;   
  showText?: boolean;
  showIcon?: boolean;
  maxWidth?: string;
  iconClass?: string;
  successIconClass?: string;
  iconSize?: string;
  showEmailIcon?: boolean;
  showPhoneIcon?: boolean;
  showWebsiteIcon?: boolean;
  showFaxIcon?: boolean;
  showLinkedInIcon?: boolean;
  showCurrencyIcon?: boolean;
  currency?: "INR" | "USD" | "EUR" | "GBP";
  showPersonIcon?: boolean;
  showDocumentIcon?: boolean;
  showTaxIcon?: boolean;
  showDateIcon?: boolean;
  showRevisionIcon?: boolean;
  onDocumentClick?: () => void; 
}

const TextCopy2: React.FC<TextCopyProps> = ({
  text,
  searchQuery,
  showEmailIcon = false,
  showPhoneIcon = false,
  showWebsiteIcon = false,
  showFaxIcon = false,
  showLinkedInIcon = false,
  showCurrencyIcon = false,
  showPersonIcon = false,
  showDocumentIcon = false,
  showTaxIcon = false,
  showDateIcon = false,
  showRevisionIcon = false,
  onDocumentClick,
  currency = "INR",
  showText = true,
  showIcon = true,
  maxWidth = "180px",
  iconClass = "ti ti-copy",
  successIconClass = "ti ti-check",
  iconSize = "14px",
}) => {
  const [copied, setCopied] = useState<boolean>(false);

  if (!text) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied successfully");

    setTimeout(() => setCopied(false), 900);
  };

  const currencyIconMap: Record<string, string> = {
  INR: "ti ti-currency-rupee",
  USD: "ti ti-currency-dollar",
  EUR: "ti ti-currency-euro",
  GBP: "ti ti-currency-pound",
};


  return (
    <div
      className="text-copy-wrapper d-flex align-items-center gap-2"
      style={{ cursor: "pointer", maxWidth }}
      onClick={handleCopy}
      title="Click to copy"
    >
        {showEmailIcon && (
  <a
    href={`mailto:${text}`}
    onClick={(e) => e.stopPropagation()} // prevent copy trigger
    title="Send email"
  >
    <i
      className="ti ti-mail"
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </a>
)}

{showPhoneIcon && text && (
  <a
    href={`tel:${text}`}
    onClick={(e) => e.stopPropagation()} // prevent row/copy click
    title="Call phone"
  >
    <i
      className="ti ti-phone"
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </a>
)}

{showPersonIcon && text && (
  <span
    title="Person"
    onClick={(e) => e.stopPropagation()} // prevent copy / row click
    style={{ display: "inline-flex", alignItems: "center" }}
  >
    <i
      className="ti ti-user"
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </span>
)}

{showDocumentIcon && (
  <span
    title="Document"
    onClick={(e) => {
      e.stopPropagation();      // ❌ prevent copy
      onDocumentClick?.();      // ✅ optional action
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      cursor: onDocumentClick ? "pointer" : "default",
    }}
  >
    <i
      className="ti ti-file-text"
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </span>
)}

{showTaxIcon && (
  <span
    title="Tax"
    onClick={(e) => e.stopPropagation()}
    style={{ display: "inline-flex", alignItems: "center" }}
  >
    <i
      className="ti ti-receipt-tax"
      style={{ fontSize: iconSize, color: "#1677ff" }}
    />
  </span>
)}

{showDateIcon && (
  <span
    title="Date"
    onClick={(e) => e.stopPropagation()}
    style={{ display: "inline-flex", alignItems: "center" }}
  >
    <i
      className="ti ti-calendar-event"
      style={{ fontSize: iconSize, color: "#1677ff" }}
    />
  </span>
)}

{showRevisionIcon && (
  <span
    title="Revision ID"
    onClick={(e) => e.stopPropagation()}
    style={{ display: "inline-flex", alignItems: "center" }}
  >
    <i
      className="ti ti-history"
      style={{ fontSize: iconSize, color: "#1677ff" }}
    />
  </span>
)}


{showWebsiteIcon && text && (
  <a
    href={text.startsWith("http") ? text : `https://${text}`}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    title="Visit website"
  >
    <i
      className="ti ti-external-link"
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </a>
)}

{showCurrencyIcon && (
  <span
    title={currency}
    onClick={(e) => e.stopPropagation()} // prevent copy / row click
    style={{ display: "inline-flex", alignItems: "center" }}
  >
    <i
      className={currencyIconMap[currency] || "ti ti-currency-rupee"}
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </span>
)}


{showLinkedInIcon && text && (
  <a
    href={text.startsWith("http") ? text : `https://${text}`}
    target="_blank"
    rel="noopener noreferrer"
    onClick={(e) => e.stopPropagation()}
    title="View LinkedIn profile"
  >
    <i
      className="ti ti-brand-linkedin"
      style={{
        fontSize: iconSize,
        color: "#0a66c2", // LinkedIn blue
      }}
    />
  </a>
)}

{showFaxIcon && text && (
  <a
    href={`fax:${text}`}
    onClick={(e) => e.stopPropagation()}
    title="Send fax"
  >
    <i
      className="ti ti-device-landline-phone"
      style={{
        fontSize: iconSize,
        color: "#1677ff",
      }}
    />
  </a>
)}


      {showText && (
        <p
          className="text-muted fw-semibold mb-0"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            margin: 0,
          }}
        >
          {searchQuery
      ? highlightMatch(text, searchQuery)
      : text}
        </p>
      )}

      {showIcon && (
  <i
    className={`copy-icon ${copied ? successIconClass : iconClass}`}
    style={{
      fontSize: iconSize,
      color: copied ? "green" : "#6c757d",
      transition: "0.2s ease",
    }}
  />
)}

    </div>
  );
};

export default TextCopy2;
